import type { Agent } from '@atproto/api'
import { MACKINAC_XRPC_URL } from '@/constants/api'
import { getServiceAuthToken } from '@/services/mackinacClient'

const GET_PREFS_NSID = 'app.mackinac.preferences.getUserPreferences'
const SET_PREF_NSID = 'app.mackinac.preferences.setUserPreference'

const USER_PREFS_QUERY_KEY_PREFIX = 'mackinacTabPrefs'

export function userPrefsQueryKey(did: string | undefined): readonly [string, string | null] {
    return [USER_PREFS_QUERY_KEY_PREFIX, did ?? null] as const
}

// --- Tab preference types ---

/** A single tab entry in the mackinac unified order */
export interface TabEntry {
    /** 'following' | AT URI for custom feeds */
    id: string
    visible: boolean
    position: number
}

/** The structured tabs preference stored under key "tabs" */
export interface TabsPreference {
    version: 1
    tabs: TabEntry[]
}

/** All user preferences returned by the preferences endpoint */
export interface UserPreferences {
    openArticlesExternally?: boolean
    tabs?: TabsPreference
    /** @deprecated Legacy server response; do not read or write. Articles moved to its own bottom-nav route (issue #2393). */
    articlesVisible?: boolean
    /** Whether the Top Stories section is hidden on the Search page. Defaults to false. */
    hideTopStories?: boolean
}

export async function fetchUserPreferences(agent: Agent): Promise<UserPreferences> {
    try {
        const token = await getServiceAuthToken(agent, GET_PREFS_NSID)
        const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${GET_PREFS_NSID}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) {
            return {}
        }
        const body = await response.json()
        return (body.preferences as UserPreferences) ?? {}
    } catch {
        return {}
    }
}

export async function saveUserPreference(
    agent: Agent,
    name: string,
    value: boolean | TabsPreference
): Promise<void> {
    const token = await getServiceAuthToken(agent, SET_PREF_NSID)
    // The setUserPreference lexicon types `value` as `unknown`, which @atproto/lexicon
    // validates as "must be an object". Wrap raw booleans in a `{ boolValue }` envelope
    // so they satisfy validation; structured prefs (e.g. tabs) are already objects.
    const wireValue = typeof value === 'boolean' ? { boolValue: value } : value
    const response = await fetch(`${MACKINAC_XRPC_URL}/xrpc/${SET_PREF_NSID}`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, value: wireValue }),
    })
    if (!response.ok) {
        const text = await response.text().catch(() => '')
        throw new Error(`setUserPreference failed: ${response.status} ${text}`)
    }
}

export const HASHTAG_TAB_ID_PREFIX = 'hashtag:'

export function tagToTabId(tag: string): string {
    return HASHTAG_TAB_ID_PREFIX + tag
}

export function tabIdToTag(tabId: string): string | null {
    if (tabId.startsWith(HASHTAG_TAB_ID_PREFIX)) {
        const tag = tabId.slice(HASHTAG_TAB_ID_PREFIX.length)
        return tag.length > 0 ? tag : null
    }
    return null
}
